/* eslint-disable @typescript-eslint/no-unused-vars */
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { Typography } from 'tfc-components';

import MainLayout from 'components/templates/MainLayout';
import NotifyModal from 'components/templates/NotifyModal';
import Confirm from 'pages/Confirm';
import Congratulation from 'pages/Congratulation';
import Register, { CheckInvitationType } from 'pages/Register';
import UserInfo, { UserInfoType } from 'pages/UserInfo';
import { checkInvitationService, checkinService } from 'services/checkin';
import {
  CheckInvitationData,
  CheckInvitationParams,
  CheckinParams,
} from 'services/checkin/types';
import returnValidateErrorMessages from 'services/common/errors';
import { formatDDMMYYYY, parseToYYYYMMDD } from 'utils/functions';
import useSchemas from 'utils/shemas';

const fieldToText = (field: string) => {
  switch (field) {
    case 'invitation_code':
      return 'Mã CME';
    case 'phone':
      return 'Số điện thoại';
    case 'full_name':
      return 'Họ và tên';
    case 'position_or_degree':
      return 'Vị trí / bằng cấp';
    case 'hospital':
      return 'Bệnh viện';
    case 'department':
      return 'Phòng ban';
    case 'provinceCode':
      return 'Tỉnh/Thành phố';
    default:
      return '';
  }
};

const Home: React.FC = () => {
  const { userInfoSchema } = useSchemas();
  const checkMethod = useForm<CheckInvitationType>({
    mode: 'onChange',
    defaultValues: {
      invitation_code: '',
      phone: '',
    },
  });
  const checkinMethod = useForm<UserInfoType>({
    mode: 'onChange',
    resolver: yupResolver(userInfoSchema),
    defaultValues: {
      invitation_code: '',
      full_name: '',
      phone: '',
      hospital: '',
      department: '',
      provinceCode: undefined,
      isRequireCme: true,
      isRegistered: false,
    },
  });

  /* States */
  const [step, setStep] = React.useState<number>(0);
  const [errorText, setErrorText] = React.useState<string>('');
  const [isCheckin, setIsCheckin] = React.useState<boolean>(false);
  const [isTemporary, setIsTemporary] = React.useState<boolean>(false);

  const [errorRegister, setErrorRegister] = React.useState<{
    message: string;
    isShow: boolean;
  }>({
    isShow: false,
    message: '',
  });

  const {
    mutate: checkInvitationMutate,
    data: checkInvitationData,
    isLoading: isLoadingCheckInvitation,
  } = useMutation(
    ['checkInvitationMutate'],
    async (data: CheckInvitationParams) => checkInvitationService({
      invitation_code: data.invitation_code ?? '',
      phone: data.phone ?? '',
    }),
    {
      onSuccess: (data: CheckInvitationData) => {
        setStep(1);
        checkinMethod.setError('invitation_code', {});
        if (data) {
          checkinMethod.reset({
            invitation_code: data?.code,
            full_name: data?.fullName,
            phone: data?.phone,
            position_or_degree: data?.positionOrDegree || '',
            hospital: data?.hospital,
            department: data?.department || '',
            provinceCode: {
              label: data?.province?.name,
              value: data?.province?.code,
            },
            dob: data?.dob ? new Date(parseToYYYYMMDD(data?.dob)) : undefined,
            isRequireCme: true,
            isRegistered: data.isRegistered,
          });
        } else {
          checkinMethod.reset({});
        }
      },
      onError: (error: any, variables: CheckInvitationParams) => {
        const errorsRes = (error as any)?.response?.data?.errors;
        if (error?.length > 0) {
          error.forEach((err: any) => {
            if (err.code === 'existed') {
              setErrorText('Đã đăng ký tham dự chương trình');
            }
          });
        } else if (errorsRes?.length > 0) {
          if (errorsRes[0]?.code === 'CAMPAIGN_NOT_FOUND') {
            checkMethod.setError('phone', {
              type: 'custom',
              message: 'Chưa có chương trình diễn ra',
            });
            return;
          }
          setStep(1);
          setIsTemporary(false);
          checkinMethod.reset({
            invitation_code: variables.invitation_code,
            phone: variables.phone,
          });
        } else {
          setErrorText('Đã có lỗi xảy ra, hãy thử lại !');
        }
      },
    }
  );

  const { mutate: checkinMutate, isLoading } = useMutation(
    ['checkinMutate'],
    async (data: CheckinParams) => checkinService(data),
    {
      onSuccess: () => {
        setStep(3);
      },
      onError(error: any) {
        const errorsRes = (error as any)?.response?.data?.errors;
        if (error.length > 0) {
          if (error[0]?.code === 'existed') {
            setIsCheckin(true);
            return;
          }
          error.forEach((err: ValidateError) => {
            checkinMethod.setError(err.field, {
              message: returnValidateErrorMessages(err, fieldToText(err.field)),
            });
            const confirmErr = ['signature'];
            if (!confirmErr.includes(err.field)) {
              setStep(1);
            }
          });
        } else if (errorsRes?.length > 0) {
          if (errorsRes[0]?.code === 'sendSMSError') {
            setErrorRegister({
              isShow: true,
              message: 'Hệ thống đã có lỗi xảy ra.<br /> Vui lòng liên hệ nhân viên Abbott để được hỗ trợ',
            });
            return;
          }
          if (errorsRes[0]?.code === 'CAMPAIGN_NOT_FOUND') {
            setErrorRegister({
              isShow: true,
              message: 'Chưa có chương trình diễn ra',
            });
            return;
          }
          setErrorRegister({
            isShow: true,
            message: 'Đã có lỗi xảy ra.<br /> Vui lòng liên hệ nhân viên Abbott để được hỗ trợ',
          });
        }
      },
    }
  );

  const handleCheck = (data: CheckInvitationType) => {
    if (
      !checkMethod.getValues('invitation_code')
      && !checkMethod.getValues('phone')
    ) {
      checkMethod.setError('invitation_code', {
        message: 'Vui lòng cung cấp thông tin',
      });
      checkMethod.setError('phone', { message: 'Vui lòng cung cấp thông tin' });
    } else {
      checkInvitationMutate({
        invitation_code: data.invitation_code,
        phone: data.phone,
      });
    }
  };

  const handleRegister = (data: CheckInvitationType) => {
    if (
      !checkMethod.getValues('invitation_code')
      && !checkMethod.getValues('phone')
    ) {
      checkMethod.setError('invitation_code', {
        message: 'Vui lòng cung cấp thông tin',
      });
      checkMethod.setError('phone', { message: 'Vui lòng cung cấp thông tin' });
    } else {
      checkInvitationMutate({
        invitation_code: data.invitation_code,
        phone: data.phone,
      });
    }
  };

  const handleConfirm = () => {
    const submitData = checkinMethod.getValues();
    if (submitData.isRegistered) {
      checkinMutate({
        invitation_code: submitData.invitation_code || '',
        phone: submitData.phone,
        full_name: submitData.full_name,
        position_or_degree: submitData.position_or_degree,
        hospital: submitData.hospital,
        department: submitData.department,
        provinceCode: submitData.provinceCode?.value,
        dob: submitData.dob ? formatDDMMYYYY(submitData.dob) : undefined,
      });
    } else {
      setStep(2);
    }
  };

  const handleComplete = () => {
    window.location.reload();
  };

  const handleSubmit = (data: CheckinParams) => {
    checkinMutate(data);
  };

  return (
    <MainLayout>
      <Typography.Heading
        textStyle="center"
        fontweight="600"
        extendClasses="t-mainLayout_subHeading fs-36x44"
      >
        {step === 2 ? 'XÁC NHẬN' : 'BIỂU MẪU'}
        {' '}
        ĐĂNG KÍ THAM DỰ
        <br />
        CHƯƠNG TRÌNH
      </Typography.Heading>
      <div className="p-register">
        {step === 0 && (
          <Typography.Text
            textStyle="italic"
            extendClasses="fs-26x36 p-register_confirmText"
          >
            Vui lòng nhập thông tin bên dưới để đăng kí tham dự chương trình
          </Typography.Text>
        )}
        {step === 1 && (
          <Typography.Text
            textStyle="italic"
            extendClasses="fs-26x36 p-register_confirmText"
          >
            {`Vui lòng ${isTemporary ? 'điền' : 'kiểm tra'
              } thông tin bên dưới và xác nhận`}
          </Typography.Text>
        )}
        {(() => {
          switch (step) {
            case 0:
              return (
                <Register
                  method={checkMethod}
                  handleCheck={handleCheck}
                  errorText={
                    checkinMethod.formState.errors.invitation_code?.message
                  }
                  handleRegister={handleRegister}
                  errorTextType={errorText}
                  loading={isLoadingCheckInvitation}
                />
              );
            case 1:
              return (
                <UserInfo
                  method={checkinMethod}
                  handleConfirm={handleConfirm}
                  isTemporary={isTemporary}
                  infoUser={checkInvitationData}
                />
              );
            case 2:
              return (
                <Confirm
                  checkinMethod={checkinMethod}
                  handleSubmit={handleSubmit}
                  isCheckin={isCheckin}
                  loading={isLoading}
                />
              );
            case 3:
              return <Congratulation handleComplete={handleComplete} />;
            default:
              return null;
          }
        })()}
      </div>
      <NotifyModal
        isOpen={errorRegister.isShow}
        variant="transparent"
        desc={errorRegister.message}
        isError
        backText="Đóng"
        handleBack={() => {
          setErrorRegister({
            ...errorRegister,
            isShow: false,
          });
        }}
      />
    </MainLayout>
  );
};

export default Home;
